import { apiGet, apiPost, apiPatch, apiDelete } from '../api-client';

// ---- Types ----

export interface FilingPortalData {
  stage?: string;
  api_number?: string;
  district?: string;
  operator?: string;
  operator_number?: string;
  lease_name?: string;
  lease_or_gas_id?: string;
  well_number?: string;
  county?: string;
  submitter?: string;
  tracking_number?: string;
  last_modified?: string;
  drilling_permit?: string;
  rule_37_case?: string;
  [key: string]: unknown;
}

export interface FilingStatus {
  id: string;
  filing_id: string;
  agency: string;
  form_type: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'revision_requested' | 'deficiency';
  agency_remarks: string;
  reviewer_name: string;
  status_date: string | null;
  portal_url: string;
  source: 'synced' | 'submitted' | 'manual';
  state: string;
  district: string;
  county: string;
  created_at: string;
  raw_portal_data: FilingPortalData;
  w3_form_id: string | null;
}

export interface ParsedIssue {
  field_name: string;
  field_value: string;
  expected_value: string;
  issue_category: string;
  issue_subcategory: string;
  severity: 'rejection' | 'deficiency' | 'revision' | 'warning';
  description: string;
  form_section: string;
  confidence: number;
  policy_references?: string[];  // optional — older records may not have it
}

export interface AcceptedCorrection {
  issue_index: number;
  field_name: string;
  applied_value: string;
  accepted_at: string;
}

export interface CorrectionInput {
  issue_index: number;
  field_name: string;
  applied_value: string;
}

export interface RejectionRecord {
  id: string;
  filing_status: string;
  agency: string;
  form_type: string;
  raw_rejection_notes: string;
  rejection_date: string | null;
  reviewer_name: string;
  parsed_issues: ParsedIssue[];
  parse_status: 'pending' | 'parsed' | 'verified';
  submitted_form_snapshot: Record<string, unknown>;
  state: string;
  district: string;
  created_at: string;
  accepted_corrections: AcceptedCorrection[];
  correction_status: 'none' | 'partial' | 'all_applied';
}

export interface Recommendation {
  id: string;
  form_type: string;
  field_name: string;
  title: string;
  description: string;
  suggested_value: string;
  scope: 'cross_tenant' | 'tenant' | 'cold_start';
  priority: 'high' | 'medium' | 'low';
  confidence?: number;
  pattern_description?: string;
  times_shown: number;
  times_accepted: number;
  acceptance_rate: number;
  is_active: boolean;
  state: string;
  district: string;
}

export interface FieldCheckResult {
  recommendations: Recommendation[];
}

export interface TrendPattern {
  id: string;
  form_type: string;
  field_name: string;
  issue_category: string;
  state: string;
  district: string;
  agency: string;
  pattern_description: string;
  occurrence_count: number;
  tenant_count: number;
  rejection_rate: number;
  is_trending: boolean;
  trend_direction: number;
  confidence: number;
}

export interface HeatmapEntry {
  district: string;
  county: string;
  rejection_count: number;
  total_filings: number;
  rejection_rate: number;
}

export interface DashboardData {
  total_filings: number;
  total_rejections: number;
  approval_rate: number;
  top_rejection_reasons: Array<{ reason: string; count: number }>;
  trending_patterns: TrendPattern[];
  recent_rejections: RejectionRecord[];
}

export interface SyncResult {
  status: 'success' | 'error';
  tenant_id: string;
  agency: string;
  total_scraped: number;
  created: number;
  updated: number;
  unchanged: number;
  wells_created: number;
  errors: number;
  error?: string;
}

export interface SyncTaskStatus {
  task_id: string;
  status: 'pending' | 'started' | 'success' | 'failure' | 'retry';
  result?: SyncResult;
  error?: string;
}

export interface PortalCredential {
  id: string;
  agency: string;
  username: string;
  last_successful_login: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Circuit-breaker fields (read-only, set by backend)
  auth_state: "ok" | "needs_reauth" | "locked";
  consecutive_login_failures: number;
  last_login_failure_at: string | null;
  last_login_error: string;
}

// ---- API Functions ----

// Filing Status
export const getFilingStatuses = (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiGet<FilingStatus[]>(`/api/intelligence/filing-status/${query}`);
};

export const getFilingStatus = (id: string) =>
  apiGet<FilingStatus>(`/api/intelligence/filing-status/${id}/`);

// Rejections
export const listRejections = (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiGet<RejectionRecord[]>(`/api/intelligence/rejections/${query}`);
};

export const getRejection = (id: string) =>
  apiGet<RejectionRecord>(`/api/intelligence/rejections/${id}/`);

export const verifyRejection = (id: string, issues: ParsedIssue[]) =>
  apiPatch<RejectionRecord>(`/api/intelligence/rejections/${id}/verify/`, { parsed_issues: issues });

export const applyCorrections = (id: string, corrections: CorrectionInput[]) =>
  apiPost<RejectionRecord>(`/api/intelligence/rejections/${id}/apply-corrections/`, corrections);

// Recommendations
export const getRecommendations = (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiGet<Recommendation[]>(`/api/intelligence/recommendations/${query}`);
};

export const checkFieldValue = (data: {
  form_type: string;
  field_name: string;
  value: string;
  state?: string;
  district?: string;
}) => apiPost<FieldCheckResult>('/api/intelligence/recommendations/check-field/', data);

export const recordInteraction = (
  id: string,
  data: { action: string; field_value_at_time?: string; dismissal_reason?: string }
) => apiPost(`/api/intelligence/recommendations/${id}/interact/`, data);

// Analytics
export const getTrends = (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiGet<TrendPattern[]>(`/api/intelligence/trends/${query}`);
};

export const getTrendsHeatmap = (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiGet<HeatmapEntry[]>(`/api/intelligence/trends/heatmap/${query}`);
};

export const getDashboard = () =>
  apiGet<DashboardData>('/api/intelligence/dashboard/');

// Filing Sync
export const syncFilings = (agency: string = 'RRC') =>
  apiPost<{ task_id: string; status: string; agency: string }>(
    '/api/intelligence/filing-status/sync/',
    { agency }
  );

export const getSyncStatus = (taskId: string) =>
  apiGet<SyncTaskStatus>(`/api/intelligence/filing-status/sync/${taskId}/`);

// Portal Credentials
export const getPortalCredentials = () =>
  apiGet<{ credentials: PortalCredential[]; has_vault_passphrase: boolean }>('/api/intelligence/credentials/');

export const createPortalCredential = (data: {
  agency: string;
  username: string;
  password: string;
  vault_passphrase?: string;
}) => apiPost<PortalCredential>('/api/intelligence/credentials/', data);

export const deletePortalCredential = (id: string) =>
  apiDelete(`/api/intelligence/credentials/${id}/`);
